const COUNTRY_CURRENCY: Record<string, string> = {
  CN: 'CNY',
  HK: 'HKD',
  IN: 'INR',
  JP: 'JPY',
  KR: 'KRW',
  TH: 'THB',
};

const GROUP_PRICING_PREFIX = 'groupPricing';
const DEFAULT_GROUP_PRICING_PREFIX = `${GROUP_PRICING_PREFIX}.default`;
const ANNUAL_PERIOD_MONTHS = 12;
const TITLE_PART_MAX_LENGTH = 120;

type GroupPricingPrice =
  | { type: 'amount'; value: number }
  | { type: 'inquiry' };

export interface GroupPricingPlan {
  monthlyPricePerPerson: GroupPricingPrice;
}

export interface GroupPricingContent {
  countryCode: string;
  basic: GroupPricingPlan;
  pro: GroupPricingPlan;
  averageTeacherCount: number;
  minimumTeacherCount: number;
  title: {
    first: string;
    second: string;
  };
  periodMessage: string;
  recommendations: string[];
}
export interface GroupPricingPlanMetrics {
  annualTotalPrice: number;
  discountPercent?: number;
  monthlyPricePerPerson: number;
}

type PricingTranslations = Readonly<Record<string, unknown>>;

const readString = (value: unknown) =>
  typeof value === 'string' ? value.trim() : undefined;

const parsePositiveInteger = (value: unknown) => {
  const parsed = Number(readString(value));
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const parseAmount = (value: unknown) => {
  const text = readString(value);
  if (!text) return undefined;

  const amount = Number(text.replace(/,/g, ''));
  return Number.isFinite(amount) && amount > 0 ? amount : undefined;
};

const readCountryValue = (
  translations: PricingTranslations,
  countryPrefix: string,
  field: string,
) => translations[`${countryPrefix}.${field}`];

const readDefaultValue = (translations: PricingTranslations, field: string) =>
  translations[`${DEFAULT_GROUP_PRICING_PREFIX}.${field}`];

const readStringWithFallback = (
  translations: PricingTranslations,
  countryPrefix: string,
  field: string,
  maxLength: number,
) => {
  const countryValue = readString(
    readCountryValue(translations, countryPrefix, field),
  );
  if (countryValue && countryValue.length <= maxLength) return countryValue;

  const defaultValue = readString(readDefaultValue(translations, field));
  if (defaultValue && defaultValue.length <= maxLength) return defaultValue;

  throw new Error(`Missing group pricing default value: ${field}`);
};

const readPositiveIntegerWithFallback = (
  translations: PricingTranslations,
  countryPrefix: string,
  field: string,
) => {
  const countryValue = parsePositiveInteger(
    readCountryValue(translations, countryPrefix, field),
  );
  if (countryValue !== undefined) return countryValue;

  const defaultValue = parsePositiveInteger(
    readDefaultValue(translations, field),
  );
  if (defaultValue !== undefined) return defaultValue;

  throw new Error(`Missing group pricing default value: ${field}`);
};

const interpolateCount = (template: string, count: number) =>
  template.replace(/\{\{count\}\}/g, String(count));

const parsePlan = (
  translations: PricingTranslations,
  countryPrefix: string,
  plan: 'basic' | 'pro',
): GroupPricingPlan => {
  const amount = parseAmount(
    readCountryValue(
      translations,
      countryPrefix,
      `${plan}.monthlyPricePerPerson`,
    ),
  );

  return {
    monthlyPricePerPerson:
      amount === undefined
        ? { type: 'inquiry' }
        : { type: 'amount', value: amount },
  };
};

/** 학교·개인 가격은 모두 1인당 월 가격이며, 학교 연간 총액은 기준 인원으로 계산한다. */
export function calculateGroupPricingPlanMetrics(
  plan: GroupPricingPlan,
  averageTeacherCount: number,
  individualMonthlyPrice: number,
): GroupPricingPlanMetrics | undefined {
  if (
    plan.monthlyPricePerPerson.type !== 'amount' ||
    averageTeacherCount <= 0
  ) {
    return undefined;
  }

  const monthlyPricePerPerson = plan.monthlyPricePerPerson.value;
  const annualTotalPrice =
    monthlyPricePerPerson * averageTeacherCount * ANNUAL_PERIOD_MONTHS;
  const discountPercent =
    individualMonthlyPrice > monthlyPricePerPerson
      ? Math.round(
          ((individualMonthlyPrice - monthlyPricePerPerson) /
            individualMonthlyPrice) *
            100,
        )
      : undefined;

  return {
    annualTotalPrice,
    monthlyPricePerPerson,
    ...(discountPercent !== undefined && { discountPercent }),
  };
}

export function getMaximumGroupDiscount(
  ...metrics: Array<GroupPricingPlanMetrics | undefined>
): number | undefined {
  const discounts = metrics.flatMap(metric =>
    metric?.discountPercent === undefined ? [] : [metric.discountPercent],
  );
  return discounts.length > 0 ? Math.max(...discounts) : undefined;
}

export function resolveGroupPricingContent(
  translations: PricingTranslations | undefined,
  countryCode: string,
): GroupPricingContent {
  const resources = translations ?? {};
  const normalizedCountryCode = /^[A-Z]{2}$/.test(
    countryCode.trim().toUpperCase(),
  )
    ? countryCode.trim().toUpperCase()
    : 'US';
  const countryPrefix = `${GROUP_PRICING_PREFIX}.${normalizedCountryCode}`;
  const minimumTeacherCount = readPositiveIntegerWithFallback(
    resources,
    countryPrefix,
    'minimumTeacherCount',
  );

  const content = {
    countryCode: normalizedCountryCode,
    basic: parsePlan(resources, countryPrefix, 'basic'),
    pro: parsePlan(resources, countryPrefix, 'pro'),
    averageTeacherCount: readPositiveIntegerWithFallback(
      resources,
      countryPrefix,
      'averageTeacherCount',
    ),
    minimumTeacherCount,
    title: {
      first: readStringWithFallback(
        resources,
        countryPrefix,
        'title.first',
        TITLE_PART_MAX_LENGTH,
      ),
      second: readStringWithFallback(
        resources,
        countryPrefix,
        'title.second',
        TITLE_PART_MAX_LENGTH,
      ),
    },
    periodMessage: readStringWithFallback(
      resources,
      countryPrefix,
      'periodMessage',
      80,
    ),
    recommendations: [
      interpolateCount(
        readStringWithFallback(
          resources,
          countryPrefix,
          'recommendation.1',
          120,
        ),
        minimumTeacherCount,
      ),
      readStringWithFallback(resources, countryPrefix, 'recommendation.2', 120),
      readStringWithFallback(resources, countryPrefix, 'recommendation.3', 120),
    ],
  } satisfies GroupPricingContent;

  return content;
}

export function getCurrencyForCountry(countryCode: string): string {
  return COUNTRY_CURRENCY[countryCode] ?? 'USD';
}
